import { useState } from 'react';
import { Navigate, useLocation, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../lib/auth-context.js';

interface FormValues { email: string; password: string; }

export function LoginPage() {
  const { user, login, mfaPending, completeMfa, cancelMfa } = useAuth();
  const [mfaCode, setMfaCode] = useState('');
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const { register, handleSubmit, formState } = useForm<FormValues>({
    defaultValues: { email: '', password: '' },
  });

  const requestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail) return;
    setResetSubmitting(true);
    setResetError(null);
    try {
      const res = await fetch('/v1/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail }),
      });
      if (!res.ok) throw new Error('Request failed');
      setResetSent(true);
    } catch (e) {
      setResetError(e instanceof Error ? e.message : 'Failed');
    } finally {
      setResetSubmitting(false);
    }
  };

  if (user) {
    const from = (location.state as { from?: string } | null)?.from ?? '/dashboard';
    return <Navigate to={from} replace />;
  }

  const onSubmit = handleSubmit(async (values) => {
    setSubmitting(true);
    setError(null);
    try {
      if (mfaPending) await completeMfa(mfaCode);
      else await login(values.email, values.password);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Login failed');
    } finally {
      setSubmitting(false);
    }
  });

  if (mfaPending) return (
    <div className="min-h-screen flex items-center justify-center bg-bg-base px-4 py-8 sm:px-6">
      <form className="w-full max-w-sm bg-bg-panel border border-border rounded-xl p-5 space-y-4" onSubmit={async (event) => {
        event.preventDefault();
        setSubmitting(true); setError(null);
        try { await completeMfa(mfaCode); }
        catch (error) { setError(error instanceof Error ? error.message : 'Verification failed'); }
        finally { setSubmitting(false); }
      }}>
        <h1 className="text-xl font-semibold text-white">Verify sign-in</h1>
        <p className="text-sm text-muted">Enter your authenticator code or a single-use recovery code.</p>
        <Field label="Authenticator or recovery code">
          <input autoFocus autoComplete="one-time-code" value={mfaCode} onChange={(event) => setMfaCode(event.target.value)} required className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-white" />
        </Field>
        {error && <div className="text-rose-400 text-sm">{error}</div>}
        <button type="submit" disabled={submitting || !mfaCode.trim()} className="w-full bg-accent disabled:opacity-50 text-accent-fg py-3 rounded-lg">{submitting ? 'Verifying…' : 'Verify'}</button>
        <button type="button" onClick={() => { cancelMfa(); setMfaCode(''); setError(null); }} className="text-sm text-muted">Cancel sign-in</button>
      </form>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg-base px-4 py-8 sm:px-6">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center gap-3 mb-6 sm:mb-8">
          <div className="w-12 h-12 rounded-xl bg-accent flex items-center justify-center text-accent-fg font-bold text-xl">1F</div>
          <h1 className="text-xl font-semibold text-white">Sign in to OPS</h1>
          <p className="text-sm text-muted">Compliance & CRM for 1Financial</p>
        </div>

        <form onSubmit={onSubmit} className="bg-bg-panel border border-border rounded-xl p-5 sm:p-6 space-y-4">
          <Field label="Email" error={formState.errors.email?.message}>
            <input
              type="email"
              autoComplete="username"
              {...register('email', { required: 'Email is required' })}
              className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 min-h-[44px] text-sm text-white placeholder-muted focus:outline-none focus:border-accent"
              placeholder="you@1financial.net"
            />
          </Field>

          <Field label="Password" error={formState.errors.password?.message}>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                {...register('password', { required: 'Password is required', minLength: { value: 8, message: 'Min 8 characters' } })}
                className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 min-h-[44px] text-sm text-white placeholder-muted focus:outline-none focus:border-accent pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted hover:text-white text-xs"
                tabIndex={-1}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </Field>

          {error && (
            <div className="text-rose-400 text-sm bg-rose-950 border border-rose-900 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg font-medium text-sm sm:text-base py-3 sm:py-2.5 min-h-[44px] rounded-lg transition-colors"
          >
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>

          <div className="text-center text-xs text-muted">
            Forgot password? <button type="button" onClick={() => setShowForgot(true)} className="text-accent hover:underline">Reset</button>
          </div>

          {showForgot && (
            <div className="bg-bg-card border border-border rounded-lg p-3 space-y-3">
              {!resetSent ? (
                <div className="space-y-3">
                  <p className="text-xs text-muted">Enter your email and we will send a password reset link if the account exists.</p>
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="you@1financial.net"
                    className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 min-h-[44px] text-sm text-white placeholder-muted focus:outline-none focus:border-accent"
                    required
                  />
                  {resetError && <div className="text-rose-400 text-xs">{resetError}</div>}
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setShowForgot(false)} className="text-xs text-muted hover:text-white px-3 py-1.5">Cancel</button>
                    <button type="button" onClick={(e) => void requestReset(e)} disabled={resetSubmitting} className="flex-1 bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-xs font-medium py-1.5 rounded-lg">
                      {resetSubmitting ? 'Sending…' : 'Send reset link'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center text-sm text-emerald-300">
                  If an account exists for that email, a reset link has been sent.
                  <button type="button" onClick={() => { setShowForgot(false); setResetSent(false); setResetEmail(''); }} className="block mx-auto mt-2 text-xs text-muted hover:text-white">Close</button>
                </div>
              )}
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs text-muted mb-1.5">{label}</div>
      {children}
      {error && <div className="text-rose-400 text-xs mt-1">{error}</div>}
    </label>
  );
}